import { gql, Order, OrderStaff, useQuery, User } from '@fluentsms/graphql';
import { useMemo } from 'react';
import { MentionableUser } from '../Mentionable';

// #region GQL
const GET_USERS = gql`
  query GetUsers($l3Id: String) {
    getUsers(l3Id: $l3Id) {
      id
      emailAddress
      displayName
      profileImage
      loginInfo {
        userStatus
      }
    }
  }
`;

const GET_ORDER = gql`
  query GetOrderStaff($id: ID!) {
    order(id: $id) {
      id
      header {
        branch
      }
      main {
        staff {
          user {
            id
          }
        }
      }
    }
  }
`;
// #endregion

// #region Hooks
function useOrderStaff(orderId: string) {
  const { data, loading } = useQuery<{ order: Order }>(GET_ORDER, {
    variables: {
      id: orderId,
    },
    skip: !orderId,
  });

  return {
    data: {
      branch: data?.order?.header?.branch ?? '',
      staff: data?.order?.main?.staff ?? [],
    } as {
      branch: string;
      staff: OrderStaff[];
    },
    loading,
  };
}

function useUsers(l3id?: string) {
  const { data, loading } = useQuery<{ getUsers: User[] }>(GET_USERS, {
    variables: {
      l3id,
    },
  });

  return { data: data?.getUsers ?? [], loading };
}

export function useMentionableUserList(orderId: string) {
  const {
    data: { branch, staff },
    loading: orderStaffLoading,
  } = useOrderStaff(orderId);
  const { data: users, loading: usersLoading } = useUsers(branch);
  const activeUsers = useMemo(() => users.filter((each) => each.loginInfo?.userStatus === 'ACTIVE'), [users]);
  const orderStaffLookup = useMemo(
    () =>
      staff.reduce((prev, next) => {
        if (next.user?.id) {
          prev[next.user?.id] = next;
        }

        return prev;
      }, {} as Record<string, OrderStaff>),
    [staff],
  );
  const userList = useMemo<MentionableUser[]>(
    () =>
      activeUsers
        .map((each) => ({
          id: each.id,
          name: each.displayName,
          email: each.emailAddress,
          avatar: each.profileImage ?? '',
          isOnOrder: Boolean(orderStaffLookup[each.id]),
          staffRole: [], // TODO
        }))
        .sort((a, b) => {
          if (a.isOnOrder && !b.isOnOrder) {
            return -1;
          } else if (b.isOnOrder && !a.isOnOrder) {
            return 1;
          } else {
            return 0;
          }
        }),
    [activeUsers, orderStaffLookup],
  );

  return {
    users: userList,
    status: orderStaffLoading || usersLoading ? 'loading' : 'loaded',
  } as {
    users: MentionableUser[];
    status: 'loading' | 'loaded';
  };
}

export function useMentionableTenantUserList() {
  const { data: users, loading: usersLoading } = useUsers();
  const activeUsers = useMemo(() => users.filter((each) => each.loginInfo?.userStatus === 'ACTIVE'), [users]);
  const userList = useMemo<MentionableUser[]>(
    () =>
      activeUsers.map((each) => ({
        id: each.id,
        name: each.displayName,
        email: each.emailAddress,
        avatar: each.profileImage ?? '',
        isOnOrder: false,
        staffRole: [], // TODO
      })),
    [activeUsers],
  );

  return {
    users: userList,
    status: usersLoading ? 'loading' : 'loaded',
  } as {
    users: MentionableUser[];
    status: 'loading' | 'loaded';
  };
}
// #endregion
